  import React, { useContext, useState, useEffect, useRef } from "react";
  import { ConfigProvider, Table as AntTable, Select, Button, Modal, Spin, message, Input, Dropdown, Menu, Checkbox, Form, Tag, Divider } from "antd";
  import { DndProvider, useDrag } from "react-dnd";
  import { HTML5Backend } from "react-dnd-html5-backend";
  import useAllocationDrop from "../../../hooks/useAllocationDrop";
  import api from "../../../authentication/axiosInstance";
  import styled from "styled-components";
  import { Box, LinearProgress } from '@mui/material';
  import { useNavigate } from 'react-router-dom';
  import { DownOutlined, EditOutlined } from '@ant-design/icons';
  import { generateSiteDetailsPDF } from "../../../utils/pdfUtils";
  import { sortVehiclesForDisplay } from "../../../utils/vehicleSortUtils";
  import { Theme, Table as RadixTable } from '@radix-ui/themes';
  import { AllocationSetupContext } from '../../../context/AllocationSetupContext';
  import { filterConfirmedAndSortSites, isSiteUnavailableInSetup } from '../siteTableUtils';
  import SiteDetailsModal, { formatArea } from './SiteDetailsModal';
  import StaffSwapModal from './StaffSwapModal';
  import { OverviewMenu } from '../overview/AllocationOverview';
  import useStaffSwap from './useStaffSwap';
  import VehicleDetailsModal, { formatPlowLabel } from './VehicleDetailsModal';
  import '@radix-ui/themes/styles.css';
  import { IconUser, IconTractor, IconLock, IconLockOpen, IconInfoCircle, IconMap } from '@tabler/icons-react';
  import styles from './EquipmentAllocationTable.module.css';

  const { Option } = Select;

  const getApiErrorMessage = (value) => {
    if (typeof value === "string") return value;
    if (Array.isArray(value)) {
      return value.map(getApiErrorMessage).find(Boolean);
    }
    if (value && typeof value === "object") {
      return Object.values(value).map(getApiErrorMessage).find(Boolean);
    }
    return null;
  };

  const STATUS_DEALLOCATION_LABELS = {
    loader_site: "Loader site",
    equipment_site: "Equipment site",
    loader_staff: "Loader staff",
    walkway_site: "Walkway Crew site",
    salting_site: "Salting Route site",
    site_state: "Finalization state",
  };

  const statusDeallocationImpact = (preview) => {
    const breakdown = Object.entries(STATUS_DEALLOCATION_LABELS)
      .filter(([key]) => preview.counts?.[key])
      .map(([key, label]) => `${label}: ${preview.counts[key]}`)
      .join(" · ") || "No existing allocations will be removed.";
    const liveOnly = preview.live_only_allocation_count > 0
      ? ` This includes ${preview.live_only_allocation_count} additional live-only allocations.`
      : "";
    const retainedCount = preview.retained_shared_loader_staff_assignment_count ?? preview.shared_loader_resources_retained ?? 0;
    const retained = ` Shared loader staff assignments left in place: ${retainedCount}${retainedCount > 0 ? " because those vehicles still serve another Confirmed site" : ""}.`;
    const locked = ` Allocations preserved in locked history: ${preview.locked_allocation_count || 0} across ${preview.locked_setup_count || 0} locked setups.`;
    const setups = preview.setups?.length > 0
      ? ` Affected setups: ${preview.setups.map((setup) => setup.setup_name).join(", ")}.`
      : "";
    return `This will remove ${preview.allocation_count} loader, equipment, staff, route, or finalization allocations across ${preview.setup_count} editable setups for ${preview.transitioning_site_count} Confirmed sites. Removed from editable/live planning: ${preview.allocation_count}. ${breakdown}.${liveOnly}${retained}${locked} Walkway Crew and Salting Route site allocations will be removed from affected editable setups. Plows and DoubleDowns will remain assigned to their vehicles. Unsaved dispatch boards containing these sites will be deleted. Saved dispatch boards will remain with the site removed from their current associations; dispatch history and unrelated board associations will remain.${setups}`;
  };

  const confirmStatusDeallocation = (preview, title) => new Promise((resolve) => {
    Modal.confirm({
      title,
      content: statusDeallocationImpact(preview),
      okText: "Apply changes",
      cancelText: "Cancel",
      onOk: () => resolve(preview.preview_hash),
      onCancel: () => resolve(null),
    });
  });

  const getPdfExportErrorMessage = (error, fallbackMessage) => {
    if (error?.response?.status === 404) {
      return "The allocation setup is no longer available. Refresh and try again.";
    }
    if (error?.response?.status === 403) {
      return "You do not have permission to export this allocation setup.";
    }
    return error.response?.data?.message || fallbackMessage;
  };

  const sanitizeForFilename = (name) => {
    if (!name) return "Setup";
    return name.replace(/[\\/:*?"<>|\r\n\t]+/g, "").replace(/\s+/g, " ").trim() || "Setup";
  };

  const buildPdfFilename = (baseFilename, setupName) => {
    const safeName = sanitizeForFilename(setupName);
    const dotIndex = baseFilename.lastIndexOf(".");
    if (dotIndex === -1) return `${baseFilename} - ${safeName}`;
    return `${baseFilename.slice(0, dotIndex)} - ${safeName}${baseFilename.slice(dotIndex)}`;
  };

  const formatVehiclePlowLabel = (vehicle) => {
    if (vehicle?.assigned_plow) {
      return formatPlowLabel(vehicle.assigned_plow);
    }
    return vehicle?.plow_size && vehicle.plow_size !== "n/a" ? vehicle.plow_size : "No plow";
  };

  const formatVehicleDoubleDownLabel = (vehicle) => {
    if (vehicle?.assigned_doubleDown) {
      const doubleDown = vehicle.assigned_doubleDown;
      return [doubleDown.doubleDown_name, doubleDown.doubleDown_model, doubleDown.doubleDown_type_name]
        .filter(Boolean).join(" · ") || `Double Down ${doubleDown.doubleDown_id}`;
    }
    return vehicle?.assigned_doubleDown_name && vehicle.assigned_doubleDown_name !== "n/a"
      ? vehicle.assigned_doubleDown_name : null;
  };

  const renderAreaChange = (before, after) => {
    if (before === after) return formatArea(after);
    return (
      <span className={styles.replaceModalChange}>
        <span className={styles.siteModalMuted}>{formatArea(before)}</span>
        <span aria-hidden="true">→</span>
        <strong>{formatArea(after)}</strong>
      </span>
    );
  };

  const isStaffAssigned = (vehicle) => {
    return vehicle.assigned_staff && vehicle.assigned_staff.length > 0;
  };

  const AllocateCell = ({
    record, allocateVehicle, allocateEquipment,
  }) => {
    const { isOver, drop } = useAllocationDrop(record,allocateVehicle,allocateEquipment,);

    return (
      <div ref={drop} style={{ width: '80%', height: 140, marginTop: "20px", display: 'flex', alignItems: 'center', justifyContent: 'center', border: isOver ? "2px dashed #034832" : "2px dashed #c9c9c9", borderRadius: '5px', color: isOver ? "#034832" : "#c9c9c9", fontSize: '16px', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        Drag Here to Allocate
      </div>
    );
  };

  const DetailsCell = ({record,allocateVehicle,allocateEquipment,showProjectDetails,
  }) => {
    const { drop } = useAllocationDrop(record,allocateVehicle,allocateEquipment);

    return (
      <div
        ref={drop}
        onClick={() => showProjectDetails(record)}
        className={styles.tableButton}
        style={{
          paddingLeft: "45px",
          paddingRight: "45px",
        }}
      >
        <IconInfoCircle size={14} />
        Details
      </div>
    );
  };

  const VehicleCard = styled.div`
    background-color: #f0f0f0;
    border-radius: 4px;
    cursor: grab;
    text-align: center;
    margin-bottom: 10px;
    width: calc(100% - 10px);
    display:flex;
    align-items:center;
    justify-content:center;
  `;

  const VehicleList = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 300px;
    max-height: 650px; /* Set a fixed height */
    overflow-y: scroll; /* Enable vertical scrolling */
    margin: 0;
    padding: 0;
  `;

  const VehicleDragCard = ({ vehicle, allocationMode, onOpen }) => {
    const canDragVehicle = allocationMode
      && vehicle.remaining_capacity !== 0
      && vehicle.assigned_plow_route_bool === false;
    const [{ isDragging }, drag] = useDrag({
      type: "vehicle",
      item: {
        id: vehicle.vehicle_id,
        type: "vehicle",
      },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: canDragVehicle,
    });

    const allocationPercentage = vehicle.vehicle_capacity > 0
      ? ((vehicle.vehicle_capacity - vehicle.remaining_capacity) / vehicle.vehicle_capacity) * 100
      : 0;
    const isPartiallyAllocated = allocationPercentage > 0 && allocationPercentage < 100;
    const progressBarColor = isPartiallyAllocated
      ? '#cdaf5e'
      : (allocationPercentage >= 100 ? '#034832' : '#aeb8b5');
    const vehicleMetadata = [
      vehicle.vehicle_subtype,
      vehicle.plow_size !== "n/a" ? formatVehiclePlowLabel(vehicle) : null,
    ].filter(Boolean).join(" · ");
    const doubleDownMetadata = vehicle.assigned_doubleDown_name && vehicle.assigned_doubleDown_name !== "n/a"
      ? vehicle.assigned_doubleDown_name
      : "";

    const getBackgroundColor = () => {
      if (vehicle.overallocated_to_site) {
        return '#bfe0e0';
      }

      if (isPartiallyAllocated) {
        return '#fff3cd';
      }
      if (allocationPercentage === 100 || vehicle.assigned_plow_route_bool) {
        return '#cce6cc';
      }
      return '#f0f0f0';
    };

    return (
      <VehicleCard
        ref={allocationMode ? drag : null}
        style={{
          opacity: isDragging ? 0.5 : 1,
          backgroundColor: getBackgroundColor(),
          cursor: canDragVehicle ? "grab" : "pointer",
          border: isPartiallyAllocated ? '1px solid #cdaf5e' : 'none',
        }}
        onClick={(event) => {
          event.preventDefault();
          onOpen(vehicle.vehicle_id);
        }}
        title={canDragVehicle
          ? "Drag to allocate or click to view details."
          : "Click to view details."}
      >
        <Box className={styles.vehicleCardContent}>
          <div className={styles.vehicleCardTopRow}>
            <span className={styles.vehicleCardName} title={vehicle.vehicle_name}>
              <span className={styles.vehicleCardIconLine}>
                <IconTractor size={14} className={styles.vehicleCardIcon} />
              </span>
              <span className={styles.vehicleCardNameText}>{vehicle.vehicle_name}</span>
              {vehicle.archived && <Tag color="default">Unavailable</Tag>}
            </span>
            <span className={styles.vehicleCardPercentage}>
              {allocationPercentage.toFixed(1)}%
            </span>
          </div>
          <span className={styles.vehicleCardMetadata} title={vehicleMetadata}>
            {vehicleMetadata}
          </span>
          <span
            className={styles.vehicleCardDoubleDown}
            title={doubleDownMetadata || undefined}
          >
            {doubleDownMetadata}
          </span>
          <Box className={styles.vehicleCardProgressRow}>
            <LinearProgress
              className={styles.vehicleCardProgress}
              variant="determinate"
              value={allocationPercentage}
              sx={{
                backgroundColor: '#dfe5e3',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: progressBarColor,
                }
              }}
            />
          </Box>
        </Box>
      </VehicleCard>
    );
  };

  const EquipmentAllocationTable = () => {
    const { refreshVersion, viewedSetup, activeSetup, loading: setupLoading, getSetupApiPath } = useContext(AllocationSetupContext);
    const [vehicleToReplace, setVehicleToReplace] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState("all");
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedSubtype, setSelectedSubtype] = useState("all");
    const [sites, setSites] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [filteredVehicles, setFilteredVehicles] = useState([]);
    const [equipment, setEquipment] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filteredSites, setFilteredSites] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [allocationMode, setAllocationMode] = useState(false);
    const [expandedRowKeys, setExpandedRowKeys] = useState([]); // Track expanded rows
    const [regions, setRegions] = useState([]);
    const navigate = useNavigate();
    const [autoCompleteOptions, setAutoCompleteOptions] = useState([]);
    const [availablePlows, setAvailablePlows] = useState([]);
    const [availableDoubleDowns, setAvailableDoubleDowns] = useState([]);
    const [popupVehicleId, setPopupVehicleId] = useState(null);
    const [deallocatingStaffId, setDeallocatingStaffId] = useState(null);
    const staffMutationRef = useRef(false);
    const [vehicleDetailsRefreshWarning, setVehicleDetailsRefreshWarning] = useState("");

    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [unlockSiteId, setUnlockSiteId] = useState(null); // Store the site to unlock
    const [password, setPassword] = useState(""); // Track the input password

    const [isExportMenuVisible, setIsExportMenuVisible] = useState(false);
    const [selectedContractStatuses, setSelectedContractStatuses] = useState(['Estimate', 'Unconfirmed', 'Confirmed']);

    const [isReplaceModalVisible, setIsReplaceModalVisible] = useState(false);
    const [selectedReplacementVehicle, setSelectedReplacementVehicle] = useState(null);
    const [replacementPreview, setReplacementPreview] = useState(null);
    const [replacementBusy, setReplacementBusy] = useState(false);
    const [replacementUncertain, setReplacementUncertain] = useState(false);
    const replacementRequest = useRef(0);
    const replacementSubmitting = useRef(false);
    const [territoryManagers, setTerritoryManagers] = useState([]);
    const [selectedSiteIds, setSelectedSiteIds] = useState([]);
    const [isBulkEditModalVisible, setIsBulkEditModalVisible] = useState(false);
    const [isBulkUpdating, setIsBulkUpdating] = useState(false);
    const [isSelectingAllSites, setIsSelectingAllSites] = useState(false);
    const [bulkForm] = Form.useForm();
    const updateBulkRegion = Form.useWatch("update_region", bulkForm);
    const updateBulkStatus = Form.useWatch("update_contract_status", bulkForm);
    const updateBulkTerritoryManager = Form.useWatch("update_assigned_territory_manager", bulkForm);
    const hasBulkFieldEnabled = Boolean(
      updateBulkRegion || updateBulkStatus || updateBulkTerritoryManager
    );

    const MAX_EXPANDED_ROWS = 3; // Set the maximum number of rows that can be expanded
    const isViewingActiveSetup = viewedSetup?.id === activeSetup?.id;
    const isLockedHistory = viewedSetup?.status === "archived";
    const selectedSetupId = viewedSetup?.id;
    const selectedSetupIdRef = useRef(selectedSetupId);
    const isSetupInitializing = setupLoading && !selectedSetupId;
    const hasNoSetup = !setupLoading && !selectedSetupId;
    const loaderPath = (suffix) => getSetupApiPath(`loaders/${suffix}`);
    const staffPath = (suffix) => getSetupApiPath(`staff/${suffix}`);


    const finalizeLoaderAllocation = async (siteId) => {
      try {
        await api.post(loaderPath(`sites/${siteId}/finalize-loader-allocation/`));
        message.success("Loader allocation finalized successfully");
        if (expandedRowKeys.includes(siteId)) {
          setExpandedRowKeys(prevKeys => prevKeys.filter(key => key !== siteId));
          if (expandedRowKeys.length === 1) {
            setAllocationMode(false);
          }
        }
        fetchSites(pagination.current, pagination.pageSize); 
      } catch (error) {
        console.error("Failed to finalize loader allocation:", error);
        message.error("Failed to finalize loader allocation");
      }
    };

    const UnlockLoaderAllocation = async (siteId, password) => {
      try {
        await api.post(loaderPath(`sites/${siteId}/unlock-loader-allocation/`), {
          password,  // Pass the password in the request body
        });
        message.success("Loader allocation unlocked successfully");
        fetchSites(pagination.current, pagination.pageSize); 
      } catch (error) {
        console.error("Failed to unlock loader allocation:", error);
        message.error("Failed to unlock loader allocation");
      }
    };

    const isAllocationPresent = (vehicle) => {
      return (vehicle.assigned_walkway_crew_bool || vehicle.assigned_salting_route_bool || vehicle.assigned_plow || vehicle.assigned_doubleDown || vehicle.assigned_plow_route_bool || 
        (vehicle.allocated_sites && vehicle.allocated_sites.length > 0) || (vehicle.number_staff_assigned > 0) ||
        (vehicle.assigned_staff && vehicle.assigned_staff.length > 0));
    }

    const availableReplacementVehicles = vehicles.filter(vehicle =>
      vehicle.vehicle_id !== vehicleToReplace?.vehicle_id && !isAllocationPresent(vehicle));
    const assignedReplacementCapacity = (vehicleToReplace?.allocated_sites || [])
      .reduce((sum, row) => sum + Number(row.allocated_capacity || 0), 0);
    const replacementLabel = (vehicle) => {
      const capacity = Number(vehicle.vehicle_capacity) || 0;
      const difference = capacity - assignedReplacementCapacity;
      const comparison = difference === 0 ? "Equal to assigned" : difference < 0
        ? `Less than assigned (${Math.abs(difference).toLocaleString()} less)`
        : `More than assigned (${difference.toLocaleString()} more)`;
      return `${vehicle.vehicle_name} (${vehicle.vehicle_subtype}) - ${capacity.toLocaleString()} sq. ft; ${comparison}`;
    };
    const closeReplacement = () => {
      replacementRequest.current += 1;
      setIsReplaceModalVisible(false);
      setVehicleToReplace(null);
      setSelectedReplacementVehicle(null);
      setReplacementPreview(null);
    };
    useEffect(() => { closeReplacement(); setReplacementUncertain(false); setReplacementBusy(false); }, [selectedSetupId]);
    const handleReplaceOpen = (vehicle_id) => {
      replacementRequest.current += 1;
      setVehicleToReplace(vehicles.find(vehicle => vehicle.vehicle_id === vehicle_id));
      setSelectedReplacementVehicle(null);
      setReplacementPreview(null);
      setReplacementBusy(false);
      setIsReplaceModalVisible(true);
    };
    const previewReplacement = async (targetId) => {
      const requestId = ++replacementRequest.current;
      setSelectedReplacementVehicle(targetId);
      setReplacementPreview(null);
      setReplacementBusy(true);
      try {
        const response = await api.post(loaderPath("replace-loader-vehicle/"), {
          vehicle_from_id: vehicleToReplace.vehicle_id, vehicle_to_id: targetId, preview: true,
        });
        if (requestId === replacementRequest.current) setReplacementPreview(response.data.summary);
      } catch (error) {
        if (requestId === replacementRequest.current) message.error(error.response?.data?.error || "Unable to preview replacement");
      } finally {
        if (requestId === replacementRequest.current) setReplacementBusy(false);
      }
    };

    const [pagination, setPagination] = useState({
      current: 1,
      pageSize: 20,
      total: 0,
      totalPages: 0
    });

    const fetchSites = async (
      page = 1,
      pageSize = 20,
      requestSetupId = selectedSetupId,
    ) => {
      setLoading(true);
      try {
        const requestPage = (requestedPage) => api.get(loaderPath("sites-by-page/"), {
          params: {
            page: requestedPage,
            pageSize,
            region: selectedRegion !== 'all' ? selectedRegion : undefined,
            contractStatus: "Confirmed",
          }
        });
        let response = await requestPage(page);
        if (selectedSetupIdRef.current !== requestSetupId) return response;
        const total = Number(response.data.total) || 0;
        const totalPages = Math.max(
          Number(response.data.totalPages)
            || Math.ceil(total / (Number(response.data.pageSize) || pageSize))
            || 1,
          1,
        );
        const validPage = total === 0 ? 1 : Math.min(Math.max(page, 1), totalPages);
        if (validPage !== page) {
          response = await requestPage(validPage);
          if (selectedSetupIdRef.current !== requestSetupId) return response;
        }

        const normalizedTotal = Number(response.data.total) || 0;
        const normalizedPageSize = Number(response.data.pageSize) || pageSize;
        const normalizedTotalPages = Math.max(
          Number(response.data.totalPages)
            || Math.ceil(normalizedTotal / normalizedPageSize)
            || 1,
          1,
        );
        setSites(filterConfirmedAndSortSites(response.data.data || []));
        setPagination({
          current: normalizedTotal === 0
            ? 1
            : Math.min(Number(response.data.page) || validPage, normalizedTotalPages),
          pageSize: normalizedPageSize,
          total: normalizedTotal,
          totalPages: normalizedTotalPages,
        });
        return response;
      } catch (error) {
        console.error("Failed to fetch sites:", error);
        if (selectedSetupIdRef.current === requestSetupId) {
          message.error("Failed to fetch sites");
        }
        return null;
      } finally {
        if (selectedSetupIdRef.current === requestSetupId) {
          setLoading(false);
        }
      }
    };

    const fetchVehicles = async (requestSetupId = selectedSetupId) => {
      try {
        const response = await api.get(loaderPath("vehicles/"));
        if (selectedSetupIdRef.current !== requestSetupId) return response;
        const fetchedVehicles = sortVehiclesForDisplay(response.data);
        setVehicles(fetchedVehicles);
        filterVehicles(fetchedVehicles, selectedSubtype, searchText); // Filter vehicles based on subtype and search
        return response;
      } catch (error) {
        console.error("Failed to fetch vehicles:", error);
        if (selectedSetupIdRef.current === requestSetupId) {
          message.error("Failed to fetch vehicles");
        }
        return null;
      }
    };

    const removeStaffFromVehicles = (currentVehicles, vehicleId, staffId) => (
      currentVehicles.map((vehicle) => {
        if (vehicle.vehicle_id !== vehicleId) return vehicle;
        const assignedStaff = (vehicle.assigned_staff || [])
          .filter((staff) => staff.staff_id !== staffId);
        return {
          ...vehicle,
          assigned_staff: assignedStaff,
          number_staff_assigned: assignedStaff.length,
        };
      })
    );

    const removeStaffFromSites = (currentSites, vehicleId, staffId) => (
      currentSites.map((site) => ({
        ...site,
        vehicles_lots: (site.vehicles_lots || []).map((vehicle) => {
          if (vehicle.vehicle_id !== vehicleId) return vehicle;
          const assignedStaff = (vehicle.assigned_staff || [])
            .filter((staff) => staff.staff_id !== staffId);
          return {
            ...vehicle,
            assigned_staff: assignedStaff,
            number_staff_assigned: assignedStaff.length,
          };
        }),
      }))
    );

    const deallocateVehicleStaff = async (vehicleId, staffId) => {
      if (staffMutationRef.current) return false;
      staffMutationRef.current = true;
      const operationSetupId = selectedSetupId;
      setDeallocatingStaffId(staffId);
      setVehicleDetailsRefreshWarning("");

      try {
        const response = await api.post(
          staffPath(`vehicles/${vehicleId}/deallocate/`),
          { staff_id: staffId },
        );
        message.success(response.data?.message || "Staff deallocated successfully");

        if (selectedSetupIdRef.current !== operationSetupId) return true;

        setVehicles((currentVehicles) => (
          removeStaffFromVehicles(currentVehicles, vehicleId, staffId)
        ));
        setFilteredVehicles((currentVehicles) => (
          removeStaffFromVehicles(currentVehicles, vehicleId, staffId)
        ));
        setSites((currentSites) => (
          removeStaffFromSites(currentSites, vehicleId, staffId)
        ));
        setFilteredSites((currentSites) => (
          removeStaffFromSites(currentSites, vehicleId, staffId)
        ));
        setSelectedProject((currentProject) => (
          currentProject
            ? removeStaffFromSites([currentProject], vehicleId, staffId)[0]
            : currentProject
        ));

        const [vehiclesRefreshed, sitesRefreshed] = await Promise.all([
          fetchVehicles(operationSetupId),
          fetchSites(pagination.current, pagination.pageSize, operationSetupId),
        ]);
        if (selectedSetupIdRef.current !== operationSetupId) return true;
        if (!vehiclesRefreshed || !sitesRefreshed) {
          setVehicleDetailsRefreshWarning(
            "Assignment removed, but the latest allocation data couldn't be refreshed.",
          );
        }
        return true;
      } catch (error) {
        console.error("Failed to deallocate staff:", error);
        message.error(
          error.response?.data?.message
          || getApiErrorMessage(error.response?.data)
          || error.message
          || "Failed to deallocate staff",
        );
        return false;
      } finally {
        staffMutationRef.current = false;
        if (selectedSetupIdRef.current === operationSetupId) {
          setDeallocatingStaffId(null);
        }
      }
    };

    const {
      swapSource,
      openSwap,
      closeSwap,
      swapping: swappingStaff,
      loadSwapData,
      swapStaff,
    } = useStaffSwap({
      getSetupApiPath,
      selectedSetupId,
      selectedSetupIdRef,
      mutationRef: staffMutationRef,
      setRefreshWarning: setVehicleDetailsRefreshWarning,
      applySwap: (data) => {
        const updates = new Map(data.vehicles.map(vehicle => [String(vehicle.vehicle_id), vehicle]));
        const updateVehicles = list => list.map(vehicle => {
          const updated = updates.get(String(vehicle.vehicle_id));
          return updated
            ? { ...vehicle, assigned_staff: updated.assigned_staff, number_staff_assigned: updated.number_staff_assigned }
            : vehicle;
        });
        const updateSites = list => list.map(site => ({ ...site, vehicles_lots: updateVehicles(site.vehicles_lots || []) }));
        setVehicles(updateVehicles);
        setFilteredVehicles(updateVehicles);
        setSites(updateSites);
        setFilteredSites(updateSites);
        setSelectedProject(site => site ? updateSites([site])[0] : site);
      },
      refreshAfterSwap: async (operationSetupId) => {
        const refreshed = await Promise.all([
          fetchVehicles(operationSetupId),
          fetchSites(pagination.current, pagination.pageSize, operationSetupId),
        ]);
        return refreshed.every(Boolean);
      },
    });

    const fetchEquipment = async () => {
      try {
        const response = await api.get(loaderPath("equipment/"));
        setEquipment(response.data);
      } catch (error) {
        console.error("Failed to fetch equipment:", error);
        message.error("Failed to fetch equipment");
      }
    };

    const fetchRegions = async () => {
      try {
        const response = await api.get(loaderPath("regions/"));
        setRegions(response.data);
      } catch (error) {
        console.error("Failed to fetch regions:", error);
        message.error("Failed to fetch regions");
      }
    };

    const fetchTerritoryManagers = async () => {
      try {
        const response = await api.get("/staff/territory_manager_staff/");
        setTerritoryManagers(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Failed to fetch territory managers:", error);
        message.error("Failed to fetch territory managers");
      }
    };

    const fetchAvailablePlows = async () => {
      try {
        const response = await api.get(loaderPath("available-plows/"));
        setAvailablePlows(response.data);
      } catch (error) {
        console.error("Failed to fetch available plows:", error);
        message.error("Failed to fetch available plows");
      }
    };

    const fetchAvailableDoubleDowns = async () => {
      try {
        const response = await api.get(loaderPath("available-double-downs/"));
        setAvailableDoubleDowns(response.data);
      } catch (error) {
        console.error("Failed to fetch available double downs:", error);
        message.error("Failed to fetch available double downs");
      }
    };

    useEffect(() => {
      selectedSetupIdRef.current = selectedSetupId;
      setPopupVehicleId(null);
      setSelectedProject(null);
      setDeallocatingStaffId(null);
      setVehicleDetailsRefreshWarning("");
    }, [selectedSetupId]);

    useEffect(() => {
      setSelectedProject((currentProject) => {
        if (!currentProject) return currentProject;
        return sites.find((site) => site.site_id === currentProject.site_id) || currentProject;
      });
    }, [sites]);

    useEffect(() => {
      if (
        popupVehicleId !== null
        && !vehicles.some((vehicle) => vehicle.vehicle_id === popupVehicleId)
      ) {
        setPopupVehicleId(null);
        setVehicleDetailsRefreshWarning("");
      }
    }, [popupVehicleId, vehicles]);

    useEffect(() => {
      if (!selectedSetupId) return;
      fetchVehicles();
      fetchEquipment();
      fetchRegions();
      fetchTerritoryManagers();
      fetchAvailablePlows();
      fetchAvailableDoubleDowns();
    }, [refreshVersion, selectedSetupId]);

    useEffect(() => {
      if (!selectedSetupId) return;
      fetchSites(1, pagination.pageSize);
    }, [selectedRegion, refreshVersion, selectedSetupId]);

    useEffect(() => {
      setSelectedSiteIds([]);
      setIsBulkEditModalVisible(false);
      bulkForm.resetFields();
    }, [selectedRegion, selectedSetupId, bulkForm]);


    const allocateVehicle = async (siteId, vehicleId) => {
      try {
        await api.post(
          loaderPath(`vehicles/${vehicleId}/allocate-vehicle/`),
          {
            site_id: siteId,
          },
        ).then(response => {
          message.success(response.data.message)
        });
        fetchSites(pagination.current, pagination.pageSize); 
        fetchVehicles();
      } catch (error) {
        console.error("Failed to assign vehicle:", error.response.data.message);
        message.error(error.response.data.message);
      }
    };

    const deallocateVehicle = async (vehicleId, siteId, surfaceType) => {
      try {
        await api.post(
          loaderPath(`vehicles/${vehicleId}/deallocate-vehicle/`),
          {
            site_id: siteId,
          }
        ).then(response => {
          message.success(response.data.message);
          fetchSites(pagination.current, pagination.pageSize); 
          fetchVehicles(); 
        });
      } catch (error) {
        console.error("Failed to deallocate vehicle:", error.response.data.message);
        message.error(error.response.data.message);
      }
    };

    const refreshReplacement = async () => {
      const refreshSetupId = selectedSetupId;
      const results = await Promise.all([fetchVehicles(), fetchSites(pagination.current, pagination.pageSize)]);
      const refreshed = results.every(Boolean);
      if (selectedSetupIdRef.current === refreshSetupId) setReplacementUncertain(!refreshed);
      return refreshed;
    };
    const replaceVehicle = async (vehicleFromId, vehicleToId) => {
      if (!vehicleToId || replacementSubmitting.current || replacementUncertain) return;
      replacementSubmitting.current = true;
      setReplacementBusy(true);
      const operationSetupId = selectedSetupId;
      try {
        const response = await api.post(loaderPath("replace-loader-vehicle/"), {
          vehicle_from_id: vehicleFromId, vehicle_to_id: vehicleToId,
        });
        if (selectedSetupIdRef.current !== operationSetupId) return;
        message.success(response.data.message);
        if (response.data.summary?.committed_reduction > 0) {
          message.warning(`Assigned capacity reduced by ${response.data.summary.committed_reduction.toLocaleString()} sq. ft. Review site coverage.`);
        }
        closeReplacement();
        await refreshReplacement();
      } catch (error) {
        if (selectedSetupIdRef.current !== operationSetupId) return;
        message.error(error.response?.data?.error || "Replacement outcome could not be confirmed. Refreshing allocations.");
        setReplacementPreview(null);
        setSelectedReplacementVehicle(null);
        setReplacementUncertain(true);
        await refreshReplacement();
      } finally {
        replacementSubmitting.current = false;
        if (selectedSetupIdRef.current === operationSetupId) setReplacementBusy(false);
      }
    };

    const allocateEquipment = async (siteId, equipmentId) => {
      try {
        await api.post(
          loaderPath(`equipment/${equipmentId}/assign-site/`),
          {
            site_id: siteId,
          },
        );
        message.success("Equipment assigned to site successfully");
        fetchSites(pagination.current, pagination.pageSize); 
        fetchEquipment();
      } catch (error) {
        console.error("Failed to assign equipment:", error);
        message.error("Failed to assign equipment");
      }
    };

    const handleSubtypeSelect = (value) => {
      setSelectedSubtype(value);
      filterVehicles(vehicles, value, searchText); // Reapply filtering with new subtype
    };

    const filterVehicles = (vehiclesList, subtype, searchText) => {
      const filteredVehicles = vehiclesList.filter(vehicle => {
        const subtypeMatch = subtype === "all" || vehicle.vehicle_subtype === subtype;
        const searchMatch = !searchText || vehicle.vehicle_name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
        return subtypeMatch && searchMatch;
      });

      setFilteredVehicles(filteredVehicles);
      
      // Update autocomplete options based on filtered results
      setAutoCompleteOptions(filteredVehicles.map(vehicle => ({
        value: vehicle.vehicle_name,
        label: vehicle.vehicle_name,
        key: vehicle.vehicle_id,
      })));
    };

    const generateLoaderAllocationPDF = async () => {
      try {
        const response = await api.get(loaderPath('pdf/by-loader/'), {
          responseType: 'blob', // Important for file download
        });

        // Create a URL for the blob and set up download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', buildPdfFilename('Allocation_by_Loader.pdf', viewedSetup?.name)); // file name
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        message.success("Current Allocation by Loader PDF generated successfully");
      } catch (error) {
        console.error("Failed to generate Current Allocation by Loader PDF:", error);
        message.error(getPdfExportErrorMessage(error, "Failed to generate Current Allocation by Loader PDF"));
      }
    };

    const generateGenericLoaderAllocationPDF = async () => {
      try {
        const response = await api.get(loaderPath('pdf/generic/'), {
          responseType: 'blob', // Important for file download
        });

        // Create a URL for the blob and set up download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', buildPdfFilename('Allocation_by_Generic_Loader.pdf', viewedSetup?.name)); // file name
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        message.success("Current Allocation by Generic Loader PDF generated successfully");
      } catch (error) {
        console.error("Failed to generate Current Allocation by Generic Loader PDF:", error);
        message.error(getPdfExportErrorMessage(error, "Failed to generate Current Allocation by Generic Loader PDF"));
      }
    };

    const generateSiteAllocationPDF = async () => {
      try {
        const statusParams = selectedContractStatuses.map(status => `status=${encodeURIComponent(status)}`).join('&');
        const response = await api.get(`${loaderPath('pdf/by-site/')}?${statusParams}`, {
          responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', buildPdfFilename('Allocation_by_Site.pdf', viewedSetup?.name));
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        message.success("Current Allocation by Site PDF generated successfully");
        setIsExportMenuVisible(false);  // Close the dropdown after generating PDF
      } catch (error) {
        console.error("Failed to generate Current Allocation by Site PDF:", error);
        message.error(getPdfExportErrorMessage(error, "Failed to generate Current Allocation by Site PDF"));
      }
    };

    const exportMenu = (
      <Menu>
        <Menu.Item key="1" onClick={generateLoaderAllocationPDF}>
          Export Allocation - By Loader
        </Menu.Item>
        <Menu.SubMenu key="2" title="Export Allocation - By Site">
          <Menu.ItemGroup>
            <div style={{ padding: '8px 16px' }}>
              <Checkbox.Group
                options={[
                  { label: 'Estimate', value: 'Estimate' },
                  { label: 'Unconfirmed', value: 'Unconfirmed' },
                  { label: 'Confirmed', value: 'Confirmed' },
                ]}
                value={selectedContractStatuses}
                onChange={setSelectedContractStatuses}
              />
            </div>
            <div style={{ padding: '8px 16px' }}>
              <Button 
                onClick={generateSiteAllocationPDF} 
                disabled={selectedContractStatuses.length === 0}
                type="primary"
              >
                Generate PDF
              </Button>
            </div>
          </Menu.ItemGroup>
        </Menu.SubMenu>
        <Menu.Item key="3" onClick={generateGenericLoaderAllocationPDF}>
          Export Generic Loader Allocation
        </Menu.Item>
      </Menu>
    );

    const showProjectDetails = (project) => {
      // Find the updated project data from 'sites' state
      const updatedProject = sites.find(site => site.site_id === project.site_id);
      setSelectedProject(updatedProject || project);
    };

    const handleModalCancel = () => {
      setSelectedProject(null);
      closeSwap();
      setVehicleDetailsRefreshWarning("");
    };

    const handleConfirmedChange = async (checked, vehicleId) => {
      if (!isViewingActiveSetup) {
        message.info("Vehicle confirmation can only be changed on the active setup.");
        return;
      }
      try {
        await api.patch(`/vehicles/${vehicleId}/`, { is_confirmed: checked });
        message.success("Vehicle confirmation status updated successfully");
    
        setSites(prevSites =>
          prevSites.map(site => ({
            ...site,
            vehicles_lots: site.vehicles_lots.map(vehicle =>
              vehicle.vehicle_id === vehicleId ? { ...vehicle, is_confirmed: checked } : vehicle
            ),
          }))
        );
    
        if (selectedProject) {
          setSelectedProject(prevProject => {
            if (!prevProject) return prevProject;
            return {
              ...prevProject,
              vehicles_lots: prevProject.vehicles_lots.map(vehicle =>
                vehicle.vehicle_id === vehicleId ? { ...vehicle, is_confirmed: checked } : vehicle
              ),
            };
          });
        }
      } catch (error) {
        console.error("Failed to update confirmation status:", error);
        message.error("Failed to update confirmation status");
      }
    };

    const handlePlowConfirmedChange = async (checked, vehicleId, plowId) => {
      if (!isViewingActiveSetup) {
        message.info("Plow confirmation can only be changed on the active setup.");
        return;
      }
      // Prefer the plow id supplied by the caller's own row. The `vehicles` list is
      // filtered by type and excludes archived rows, so a loader can appear in a
      // site's allocations without being present here; falling back to it silently
      // would make the checkbox a no-op.
      const resolvedPlowId = plowId
        ?? vehicles.find((v) => v.vehicle_id === vehicleId)?.assigned_plow?.plow_id;
      if (!resolvedPlowId) {
        message.error("Could not identify the plow assigned to this loader");
        return;
      }
      try {
        await api.patch(`/plows/${resolvedPlowId}/`, { is_confirmed: checked });
        message.success("Plow confirmation status updated successfully");

        // Update the 'vehicles' state
        setVehicles((prevVehicles) =>
          prevVehicles.map((v) =>
            v.vehicle_id === vehicleId && v.assigned_plow
              ? {
                  ...v,
                  assigned_plow: { ...v.assigned_plow, is_confirmed: checked },
                }
              : v
          )
        );
    
        // Update the 'sites' state to persist the change
        setSites((prevSites) =>
          prevSites.map((site) => ({
            ...site,
            vehicles_lots: site.vehicles_lots.map((v) =>
              v.vehicle_id === vehicleId && v.assigned_plow
                ? {
                    ...v,
                    assigned_plow: { ...v.assigned_plow, is_confirmed: checked },
                  }
                : v
            ),
          }))
        );
    
        // Update 'selectedProject' if the modal is open
        if (selectedProject) {
          setSelectedProject((prevProject) => {
            if (!prevProject) return prevProject;
            return {
              ...prevProject,
              vehicles_lots: prevProject.vehicles_lots.map((v) =>
                v.vehicle_id === vehicleId && v.assigned_plow
                  ? {
                      ...v,
                      assigned_plow: { ...v.assigned_plow, is_confirmed: checked },
                  }
                  : v
              ),
            };
          });
        }
      } catch (error) {
        console.error("Failed to update plow confirmation status:", error);
        message.error("Failed to update plow confirmation status");
      }
    };

    const getVehiclePlowValue = (vehicle) => vehicle?.assigned_plow?.plow_id || "n/a";

    const getPlowOptionsForVehicle = (vehicle) => {
      const options = [...availablePlows];
      if (vehicle?.assigned_plow && !options.some((plow) => plow.plow_id === vehicle.assigned_plow.plow_id)) {
        options.unshift(vehicle.assigned_plow);
      }
      return options;
    };

    // Reusable VehiclesTable component
    const VehiclesTable = ({ title, vehicles, isOverallocated, deallocateVehicle, record, availablePlows, fetchVehicles, fetchAvailablePlows }) => {
      const handlePlowChange = async (vehicleId, value) => {
        if (value === "n/a") {
          await handleDeallocatePlow(vehicleId);
          return;
        }
        try {
          await api.post(loaderPath(`vehicles/${vehicleId}/allocate-plow/`), {
            plow_id: value,
          });
        } catch (error) {
          console.error("Failed to assign plow:", error);
          message.error(error.response?.data?.error || "Failed to assign plow");
          return;
        }
        fetchVehicles();
        fetchAvailablePlows();
        fetchSites(pagination.current, pagination.pageSize);
        message.success("Plow assigned successfully");
      };

      const handleDeallocatePlow = async (vehicleId) => {
        try {
          await api.post(loaderPath(`vehicles/${vehicleId}/deallocate-plow/`));
          fetchVehicles();
          fetchAvailablePlows();
          fetchSites(pagination.current, pagination.pageSize); 
          message.success("Plow deallocated successfully");
        } catch (error) {
          console.error("Failed to deallocate plow:", error);
          message.error(error.response?.data?.error || "Failed to deallocate plow");
        }
      };

      return (
        <Box style={{ marginBottom: "8px" }}>
          <p style={{ fontWeight: "bold", marginBottom: "4px", fontSize: "14px" }}>{title}</p>
          {vehicles.length > 0 ? (
            <RadixTable.Root size="1">
              <RadixTable.Header>
                <RadixTable.Row>
                  <RadixTable.ColumnHeaderCell style={{ width: "15%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>Vehicle</RadixTable.ColumnHeaderCell>
                  <RadixTable.ColumnHeaderCell style={{ width: "20%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>Allocated Capacity</RadixTable.ColumnHeaderCell>
                  <RadixTable.ColumnHeaderCell style={{ width: "20%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>Assigned Staff</RadixTable.ColumnHeaderCell>
                  <RadixTable.ColumnHeaderCell style={{ width: "20%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>Plow</RadixTable.ColumnHeaderCell>
                  {!isLockedHistory && <RadixTable.ColumnHeaderCell style={{ width: "25%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>Actions</RadixTable.ColumnHeaderCell>}
                </RadixTable.Row>
              </RadixTable.Header>

              <RadixTable.Body>
                {vehicles.map((vehicle) => (
                  <RadixTable.Row key={vehicle.vehicle_id}>
                    <RadixTable.Cell style={{ width: "15%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>
                      {vehicle.vehicle_name} ({vehicle.vehicle_subtype})
                    </RadixTable.Cell>
                    <RadixTable.Cell style={{ width: "20%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>
                      {vehicle.allocated_capacity.toLocaleString()} sq. ft{isOverallocated ? " (Overallocated)" : ""}
                    </RadixTable.Cell>
                    <RadixTable.Cell style={{ width: "20%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>
                      {vehicle.assigned_staff && vehicle.assigned_staff.length > 0 ? (
                              <ul style={{ paddingLeft:0, margin: 0,}}>
                              {vehicle.assigned_staff.map((staff, index) => (
                                  <li key={index} style={{ listStyle: "none", marginBottom: "4px" }}>
                                    <strong>{staff.name}</strong> - {staff.position}, Rating: {staff.staff_rating ? staff.staff_rating : "Unspecified"}
                                  </li>
                              ))}
                              </ul>
                          ) : (
                              "Unassigned"
                          )}
                    </RadixTable.Cell>
                    <RadixTable.Cell style={{ width: "20%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>
                      {isLockedHistory ? (
                        vehicle.assigned_plow ? formatPlowLabel(vehicle.assigned_plow) : "n/a"
                      ) : (
                        <Select
                          placeholder="Select Plow"
                          style={{ width: '100%' }}
                          onChange={(value) => handlePlowChange(vehicle.vehicle_id, value)}
                          value={getVehiclePlowValue(vehicle)}
                          size="small"
                        >
                          <Select.Option value="n/a">n/a</Select.Option>
                          {getPlowOptionsForVehicle(vehicle).map(plow => (
                            <Select.Option key={plow.plow_id} value={plow.plow_id}>
                              {formatPlowLabel(plow)}
                            </Select.Option>
                          ))}
                        </Select>
                      )}
                    </RadixTable.Cell>
                    {!isLockedHistory && <RadixTable.Cell style={{ width: "25%", textAlign: "left", padding: "4px 8px", fontSize: "12px" }}>
                      <Button
                        onClick={() => deallocateVehicle(vehicle.vehicle_id, record.site_id, "lots")}
                        type="primary"
                        danger
                        size="small"
                        style={{ marginRight: '4px' }}
                        disabled = {isAllocationPresent(vehicle) || isStaffAssigned(vehicle)}
                        title={isAllocationPresent(vehicle) || isStaffAssigned(vehicle) ? "Deallocate staff and plows before deallocating vehicle from site" : ""}
                      >
                        Deallocate
                      </Button>
                      {getVehiclePlowValue(vehicle) !== "n/a" && (
                        <Button
                          onClick={() => handleDeallocatePlow(vehicle.vehicle_id)}
                          size="small"
                        >
                          Remove Plow
                        </Button>
                      )}
                    </RadixTable.Cell>}
                  </RadixTable.Row>
                ))}
              </RadixTable.Body>
            </RadixTable.Root>
          ) : (
            <p style={{ fontSize: "12px" }}>{isOverallocated ? "No Overallocated Parking Lots Assigned Vehicles" : "No Parking Lots Assigned Vehicles"}</p>
          )}
        </Box>
      );
    };

    const expandedRowRender = (record) => {
      // Separate allocated and overallocated vehicles
      const allocatedVehicles = record.vehicles_lots.filter(vehicle => !vehicle.overallocated_to_site);
      const overallocatedVehicles = record.vehicles_lots.filter(vehicle => vehicle.overallocated_to_site);

      return (
        <Box style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
          {!isLockedHistory && <Box style={{ flex: "0 0 400px", display: "flex", alignItems: "center" }}>
            <AllocateCell
              record={record}
              allocateVehicle={allocateVehicle}
              allocateEquipment={allocateEquipment}
            />
          </Box>}

          {/* Tables - Right Side */}
          <Box style={{ flex: "1", display: "flex", flexDirection: "column" }}>
            {/* Parking Lots Assigned Vehicles Table */}
            <VehiclesTable
              title="Parking Lots Assigned Vehicles:"
              vehicles={allocatedVehicles}
              isOverallocated={false}
              deallocateVehicle={deallocateVehicle}
              record={record}
              availablePlows={availablePlows}
              fetchVehicles={fetchVehicles}
              fetchAvailablePlows={fetchAvailablePlows}
            />

            {/* Overallocated Parking Lots Assigned Vehicles Table */}
            <VehiclesTable
              title="Overallocated Parking Lots Assigned Vehicles:"
              vehicles={overallocatedVehicles}
              isOverallocated={true}
              deallocateVehicle={deallocateVehicle}
              record={record}
              availablePlows={availablePlows}
              fetchVehicles={fetchVehicles}
              fetchAvailablePlows={fetchAvailablePlows}
            />
          </Box>
        </Box>
      );
    };

    const columns = [
      { 
        title: "Customer Name", 
        dataIndex: "client_name", 
        key: "client_name",
        width: 150
      },
      { 
        title: "Site Name", 
        dataIndex: "site_name", 
        key: "site_name",
        width: 200,
        render: (name, record) => <span>{name} {isSiteUnavailableInSetup(record) && <Tag color="default">Unavailable</Tag>}</span>,
      },
      {
        title: "Locked",
        key: "locked",
        width: 81,
        render: (_, record) => (
          <Box>
            {record.loader_allocation_finalized ? (
              <div style={{ backgroundColor: '#E5F2EC', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #034832', borderRadius: '4px', color: "#034832" }}>
                <IconLock size={18} stroke={2} />
              </div>
              
            ) : (
              <div style={{ backgroundColor: '#f5f5f5', height: '30px', width: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #ebeaea', borderRadius: '4px', color: "#034832" }}>
                <IconLockOpen size={18} stroke={2} />
              </div>
            )}
          </Box>
        ),
      },
      {
        title: "Lots Area (sq. ft)",
        dataIndex: "parking_lots_sqft",
        key: "parking_lots_sqft",
        width: 260,
        render: (lotsArea, record) => {
          const allocationPercentage = (record.parking_lots_sqft_allocated / lotsArea) * 100;
          const displayPercentage = allocationPercentage > 100 ? 100 : allocationPercentage;

          return (
            <span>
              {record.parking_lots_sqft_allocated.toLocaleString()} / {lotsArea.toLocaleString()}
              <Box style={{ display: "flex", alignItems: "center", width: 250 }}>
                <LinearProgress 
                  style={{ flex: 9, marginRight: 8 }} 
                  variant="determinate" 
                  value={displayPercentage}
                />
                <Box style={{ flex: 1 }}>
                  {allocationPercentage.toFixed(1)}%
                </Box>
              </Box>
              {allocationPercentage > 100 && (
                <Box style={{ color: 'Orange', marginTop: 4 }}>
                  Over Allocated
                </Box>
              )}
            </span>
          )
        },
      },
      {
        title: "Overallocated Loaders",
        width: 127,
        dataIndex: "num_overallocated_loaders",
        key: "num_overallocated_loaders",
        render: (text, record) => (
          <Box style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', width: '100%' }}>
            {text}
          </Box>
        )
      },
      {
        title: "Actions",
        key: "actions",
        render: (_, record) => (
          <Box style={{ display: "flex", alignItems: "flex-start", gap: "5px", flexDirection: 'column', justifyContent: 'flex-start' }}>
            {!isLockedHistory && (
              <div
                className={styles.tableButtonGold}
                style={{ width: '135px' }}
                onClick={() => {
                  if (record.loader_allocation_finalized) {
                    setUnlockSiteId(record.site_id);
                    setPasswordModalVisible(true);
                  } else {
                    finalizeLoaderAllocation(record.site_id);
                  }
                }}
              >
                {record.loader_allocation_finalized ? "Unlock Finalization" : "Finalize Allocation"}
              </div>
            )}
            <DetailsCell
              record={record}
              allocateVehicle={allocateVehicle}
              allocateEquipment={allocateEquipment}
              showProjectDetails={showProjectDetails}
            />
           
          </Box>
        ),
      },
    ];

    const onTableExpand = (expanded, record) => {
      if (expanded) {
        setExpandedRowKeys(prevKeys => {
          if (prevKeys.length < MAX_EXPANDED_ROWS) {
            setAllocationMode(!isLockedHistory);
            return [...prevKeys, record.site_id];
          }
          message.warning(`You can only expand up to ${MAX_EXPANDED_ROWS} sites at a time.`);
          return prevKeys;
        });
      } else {
        setExpandedRowKeys(prevKeys => {
          const newKeys = prevKeys.filter(key => key !== record.site_id);
          if (newKeys.length === 0) {
            setAllocationMode(false);
          }
          return newKeys;
        });
      }
    };

    const handleSearch = (value) => {
      setSearchText(value);
      filterVehicles(vehicles, selectedSubtype, value); // Reapply filtering with new search text
    };

    const handleSelect = (value) => {
      const selectedVehicle = filteredVehicles.find(vehicle => vehicle.vehicle_name === value);
      if (selectedVehicle) {
        setFilteredVehicles([selectedVehicle]); // Set the filteredVehicles to just the selected one
      }
    };

    const applyFilters = () => {
      const filtered = sites.filter((site) => {
        const filterText = site.site_name
          .toLowerCase()
          .includes(searchText.toLowerCase());
        return filterText;
      });
      setFilteredSites(filtered);
    };

    // const debouncedSearch = debounce((value) => {
    //   handleSearch(value);
    // }, 500);

    const handleSwitchMapView = () => {
      navigate('/map');
    };

    const rowSelection = {
      selectedRowKeys: selectedSiteIds,
      preserveSelectedRowKeys: true,
      onSelect: (record, selected) => {
        setSelectedSiteIds((currentIds) => (
          selected
            ? Array.from(new Set([...currentIds, record.site_id]))
            : currentIds.filter((siteId) => siteId !== record.site_id)
        ));
      },
      onSelectAll: (selected, _selectedRows, changedRows) => {
        const changedSiteIds = changedRows.map((site) => site.site_id);
        setSelectedSiteIds((currentIds) => (
          selected
            ? Array.from(new Set([...currentIds, ...changedSiteIds]))
            : currentIds.filter((siteId) => !changedSiteIds.includes(siteId))
        ));
      },
    };

    const selectAllFilteredSites = async () => {
      setIsSelectingAllSites(true);
      try {
        const response = await api.get(loaderPath("sites-by-page/"), {
          params: {
            page: 1,
            pageSize: Math.max(pagination.total, 1),
            region: selectedRegion !== "all" ? selectedRegion : undefined,
            contractStatus: "Confirmed",
          },
        });
        setSelectedSiteIds(
          (response.data?.data || []).map((site) => site.site_id)
        );
      } catch (error) {
        console.error("Failed to select all loader sites:", error);
        message.error("Failed to select all sites");
      } finally {
        setIsSelectingAllSites(false);
      }
    };

    const openBulkEditModal = () => {
      bulkForm.resetFields();
      setIsBulkEditModalVisible(true);
    };

    const closeBulkEditModal = () => {
      if (isBulkUpdating) return;
      setIsBulkEditModalVisible(false);
      bulkForm.resetFields();
    };

    const runStatusChangeWithPreview = async ({
      siteIds,
      targetStatus,
      title,
      mutate,
    }) => {
      let previewResponse = await api.post("/sites/status-deallocation-preview/", {
        site_ids: siteIds,
        target_status: targetStatus,
      });
      let preview = previewResponse.data;

      while (preview?.transitioning_site_count > 0) {
        const previewHash = await confirmStatusDeallocation(preview, title);
        if (!previewHash) return null;
        try {
          return await mutate(previewHash);
        } catch (error) {
          if (error.response?.status === 409 && error.response?.data?.preview) {
            preview = error.response.data.preview;
            message.warning("Allocation impact changed. Review and confirm again.");
            continue;
          }
          throw error;
        }
      }
      return mutate(null);
    };

    const applyBulkUpdate = async (updates, statusPreviewHash = null) => {
      setIsBulkUpdating(true);
      try {
        const response = await api.patch("/sites/bulk-update/", {
          site_ids: selectedSiteIds,
          updates,
          ...(statusPreviewHash ? { status_preview_hash: statusPreviewHash } : {}),
        });
        await Promise.all([
          fetchSites(pagination.current, pagination.pageSize),
          fetchVehicles(),
          fetchEquipment(),
          fetchRegions(),
        ]);
        message.success(
          response.data?.message || `${selectedSiteIds.length} sites updated successfully.`
        );
        setSelectedSiteIds([]);
        setIsBulkEditModalVisible(false);
        bulkForm.resetFields();
        return response;
      } catch (error) {
        if (error.response?.status === 409 && error.response?.data?.preview) {
          throw error;
        }
        console.error("Failed to bulk update sites:", error);
        const errorData = error.response?.data;
        message.error(getApiErrorMessage(errorData) || "Failed to bulk update sites");
        if (Array.isArray(errorData?.unavailable_site_ids)) {
          const unavailableSiteIds = new Set(errorData.unavailable_site_ids);
          setSelectedSiteIds((currentIds) => (
            currentIds.filter((siteId) => !unavailableSiteIds.has(siteId))
          ));
          fetchSites(pagination.current, pagination.pageSize);
        }
      } finally {
        setIsBulkUpdating(false);
      }
    };

    const handleBulkEditSubmit = async () => {
      try {
        const values = await bulkForm.validateFields();
        const updates = {};

        if (values.update_region) {
          updates.region = values.region.trim();
        }
        if (values.update_contract_status) {
          updates.contract_status = values.contract_status;
        }
        if (values.update_assigned_territory_manager) {
          updates.assigned_territory_manager = values.assigned_territory_manager;
        }

        if (Object.keys(updates).length === 0) {
          message.warning("Select at least one field to update.");
          return;
        }

        const fieldLabels = {
          region: "Region",
          contract_status: "Status",
          assigned_territory_manager: "Territory Manager",
        };
        const changedFields = Object.keys(updates).map((field) => fieldLabels[field]).join(", ");
        if (updates.contract_status && updates.contract_status !== "Confirmed") {
          await runStatusChangeWithPreview({
            siteIds: selectedSiteIds,
            targetStatus: updates.contract_status,
            title: `Update ${selectedSiteIds.length} selected sites?`,
            mutate: (previewHash) => applyBulkUpdate(updates, previewHash),
          });
        } else {
          Modal.confirm({
            title: `Update ${selectedSiteIds.length} selected sites?`,
            content: `The following fields will be updated: ${changedFields}.`,
            okText: "Apply changes",
            cancelText: "Cancel",
            onOk: () => applyBulkUpdate(updates),
          });
        }
      } catch (error) {
        if (!error?.errorFields) {
          console.error("Failed to validate bulk site update:", error);
          message.error("Failed to validate bulk site update");
        }
      }
    };

    const popupVehicle = popupVehicleId === null
      ? null
      : vehicles.find((vehicle) => vehicle.vehicle_id === popupVehicleId) || null;
    const selectedSitePageWarning = selectedProject
      && !sites.some((site) => site.site_id === selectedProject.site_id)
      ? "This site is no longer in the current results. Showing the last available details."
      : "";

    const openVehiclePopup = (vehicleId) => {
      setVehicleDetailsRefreshWarning("");
      setPopupVehicleId(vehicleId);
    };

    if (isSetupInitializing) {
      return (
        <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, minHeight: 240 }}>
          <Spin />
          <span>Loading allocation setup...</span>
        </Box>
      );
    }

    if (hasNoSetup) {
      return (
        <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240 }}>
          No allocation setup available.
        </Box>
      );
    }

    return (
      <ConfigProvider theme={{ token: { colorLinkHover: "#034832", colorPrimary: "#034832", controlItemBgActive: "#E4F2EB" } }}>
        <Theme>
          <DndProvider backend={HTML5Backend}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', justifyContent: 'center', padding: '20px',}}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '15px',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  width: '300px',
                }}
              >
                <span style={{fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: '600', fontSize: '28px'}}>
                  Loaders
                </span>
                <Select
                  placeholder="Select Subtype"
                  defaultValue="all"
                  style={{ width: 'calc(100% - 20px)' }}
                  onChange={handleSubtypeSelect}
                >
                  <Option value="all">All Subtypes</Option>
                  {vehicles
                    .map((vehicle) => vehicle.vehicle_subtype)
                    .filter((value, index, self) => self.indexOf(value) === index)
                    .map((subtype) => (
                      <Option key={subtype} value={subtype}>
                        {subtype}
                      </Option>
                    ))}
                </Select>
                <Input
                  style={{ width: '100%' }}
                  onSearch={handleSearch}
                  onSelect={handleSelect}
                  placeholder="Search loaders"
                  value={searchText}
                  onChange={(e) => handleSearch(e.target.value)}
                />
                <VehicleList>
                  {filteredVehicles
                    .map((vehicle) => (
                      <VehicleDragCard
                        key={vehicle.vehicle_id}
                        vehicle={vehicle}
                        allocationMode={allocationMode && !isLockedHistory}
                        onOpen={openVehiclePopup}
                      />
                    ))}
                </VehicleList>
              </div>
              <div style={{ width: 'calc(100% - 320px)', marginLeft: '20px'}}>
                <div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <Select
                      placeholder="Select Region"
                      aria-label="Filter loader sites by region"
                      defaultValue="all"
                      style={{ width: 150 }}
                      onChange={(value) =>  setSelectedRegion(value)}
                    >
                      <Option value="all">All Regions</Option>
                      {regions.map((region) => (
                        <Option key={region} value={region}>
                          {region.charAt(0).toUpperCase() + region.slice(1)}
                        </Option>
                      ))}
                    </Select>
                    <Dropdown overlay={exportMenu} trigger={['click']}>
                      <Button style={{ width: 150 }}>
                        PDF Export <DownOutlined />
                      </Button>
                    </Dropdown>
                  </div>

                  <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                    <div className={styles.tableButton} onClick={handleSwitchMapView} style={{ fontSize: '14px', padding: '8px 15px' }}>
                      <IconMap size={16}/>
                      Map View
                    </div>
                    <div className={styles.tableButton} onClick={() => navigate('/staffAllocation')} style={{ fontSize: '14px', padding: '8px 15px' }}>
                      <IconUser size={16}/>
                      Staff Allocation
                    </div>
                    <OverviewMenu setupId={selectedSetupId} />
                  </div>
                </div>
                {pagination.total > 0 && !isLockedHistory && (
                  <div className={styles.bulkActionBar}>
                    <span className={styles.selectedCount}>
                      {selectedSiteIds.length} {selectedSiteIds.length === 1 ? "site" : "sites"} selected
                    </span>
                    <div className={styles.bulkActionButtons}>
                      <Button
                        onClick={selectAllFilteredSites}
                        loading={isSelectingAllSites}
                      >
                        Select all {pagination.total} sites
                      </Button>
                      <Button
                        onClick={() => setSelectedSiteIds([])}
                        disabled={selectedSiteIds.length === 0}
                      >
                        Clear selection
                      </Button>
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={openBulkEditModal}
                        disabled={selectedSiteIds.length === 0}
                      >
                        Bulk Edit
                      </Button>
                    </div>
                  </div>
                )}
                <Spin
                  spinning={loading || isBulkUpdating}
                  size="large"
                  tip={isBulkUpdating ? "Updating loader allocations..." : undefined}
                >
                <>
                  <style>{`
                    .locked-row {
                      background-color: #f0f0f0; !important;
                    }
                  `}
                  </style>
                  <style>{`
                    .ant-table-tbody > tr:hover > td {
                      background: none !important; /* Removes hover background */
                    }
                  `}</style>
                  <AntTable
                    dataSource={sites}
                    columns={columns}
                    rowKey="site_id"
                    rowSelection={isLockedHistory ? undefined : rowSelection}
                    defaultPageSize={20}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      total: pagination.total,
                      onChange: (page, pageSize) => {
                        fetchSites(page, pageSize);
                      },
                      showSizeChanger: true,
                      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                    }}
                    scroll={{ y: 690 }}
                    rowClassName={(record) =>
                      record.loader_allocation_finalized ? 'locked-row' : ''
                    }
                    expandable={{
                      expandedRowRender,
                      expandedRowKeys: expandedRowKeys,
                      onExpand: (expanded, record) => {
                        if (isLockedHistory || !record.loader_allocation_finalized) {
                          onTableExpand(expanded, record);
                        }
                      },
                      expandIcon: ({ expanded, onExpand, record }) =>
                        !isLockedHistory && record.loader_allocation_finalized ? null : (
                          <Button
                            onClick={(e) => onExpand(record, e)}
                            style={{ border: 'none', backgroundColor: 'transparent' }}
                            disabled={!expanded && expandedRowKeys.length >= MAX_EXPANDED_ROWS}
                          >
                            {expanded ? '-' : '+'}
                          </Button>
                        ),
                    }}
                  />
                </>
                </Spin>
                <Modal
                  title="Bulk Edit Sites"
                  open={isBulkEditModalVisible}
                  onCancel={closeBulkEditModal}
                  onOk={handleBulkEditSubmit}
                  okText="Review changes"
                  confirmLoading={isBulkUpdating}
                  okButtonProps={{
                    disabled:
                      !hasBulkFieldEnabled ||
                      selectedSiteIds.length === 0,
                  }}
                  cancelButtonProps={{ disabled: isBulkUpdating }}
                >
                  <p>
                    Update selected fields for {selectedSiteIds.length}{" "}
                    {selectedSiteIds.length === 1 ? "site" : "sites"}. Unchecked fields will not change.
                  </p>
                  <Form
                    form={bulkForm}
                    layout="vertical"
                    initialValues={{
                      update_region: false,
                      update_contract_status: false,
                      update_assigned_territory_manager: false,
                    }}
                  >
                    <Form.Item name="update_region" valuePropName="checked">
                      <Checkbox>Update Region</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="region"
                      label="Region"
                      dependencies={["update_region"]}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator: (_, value) => {
                            if (!getFieldValue("update_region")) return Promise.resolve();
                            if (!value || !value.trim()) {
                              return Promise.reject("Please enter a region");
                            }
                            if (value.trim().length > 50) {
                              return Promise.reject("Region must be 50 characters or fewer");
                            }
                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <Input disabled={!updateBulkRegion} maxLength={50} />
                    </Form.Item>

                    <Form.Item name="update_contract_status" valuePropName="checked">
                      <Checkbox>Update Status</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="contract_status"
                      label="Status"
                      dependencies={["update_contract_status"]}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator: (_, value) => (
                            getFieldValue("update_contract_status") && !value
                              ? Promise.reject("Please select a status")
                              : Promise.resolve()
                          ),
                        }),
                      ]}
                    >
                      <Select disabled={!updateBulkStatus} placeholder="Select status">
                        <Option value="Estimate">Estimate</Option>
                        <Option value="Unconfirmed">Unconfirmed</Option>
                        <Option value="Confirmed">Confirmed</Option>
                      </Select>
                    </Form.Item>

                    <Form.Item name="update_assigned_territory_manager" valuePropName="checked">
                      <Checkbox>Update Territory Manager</Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="assigned_territory_manager"
                      label="Territory Manager"
                      dependencies={["update_assigned_territory_manager"]}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator: (_, value) => (
                            getFieldValue("update_assigned_territory_manager") && !value
                              ? Promise.reject("Please select a territory manager")
                              : Promise.resolve()
                          ),
                        }),
                      ]}
                    >
                      <Select
                        disabled={!updateBulkTerritoryManager}
                        showSearch
                        placeholder="Select a territory manager"
                        filterOption={(inputValue, option) =>
                          (option?.label ?? "").toLowerCase().includes(inputValue.toLowerCase())
                        }
                        options={territoryManagers.map((manager) => ({
                          value: manager.name,
                          label: manager.name,
                        }))}
                      />
                    </Form.Item>
                  </Form>
                </Modal>
                <SiteDetailsModal
                  open={Boolean(selectedProject)}
                  site={selectedProject}
                  isViewingActiveSetup={isViewingActiveSetup}
                  onClose={handleModalCancel}
                  onVehicleConfirmationChange={handleConfirmedChange}
                  onPlowConfirmationChange={handlePlowConfirmedChange}
                  onDownloadPdf={generateSiteDetailsPDF}
                  vehicles={vehicles}
                  onDeallocateStaff={deallocateVehicleStaff}
                  onSwapStaff={openSwap}
                  staffMutationBusy={swappingStaff}
                  deallocatingStaffId={deallocatingStaffId}
                  refreshWarning={vehicleDetailsRefreshWarning || selectedSitePageWarning}
                  setupName={viewedSetup?.name}
                  readOnly={isLockedHistory}
                />
                {popupVehicle && (
                  <VehicleDetailsModal
                    vehicle={popupVehicle}
                    onClose={() => {
                      setPopupVehicleId(null);
                      closeSwap();
                      setVehicleDetailsRefreshWarning("");
                    }}
                    availablePlows={availablePlows}
                    availableDoubleDowns={availableDoubleDowns}
                    loaderPath={loaderPath}
                    fetchVehicles={fetchVehicles}
                    fetchAvailablePlows={fetchAvailablePlows}
                    fetchAvailableDoubleDowns={fetchAvailableDoubleDowns}
                    fetchSites={fetchSites}
                    pagination={pagination}
                    onReplaceOpen={(vehicleId) => {
                      setPopupVehicleId(null);
                      closeSwap();
                      setVehicleDetailsRefreshWarning("");
                      handleReplaceOpen(vehicleId);
                    }}
                    onDeallocateStaff={deallocateVehicleStaff}
                    onSwapStaff={(staff) => openSwap({
                      ...staff,
                      vehicle_id: popupVehicle.vehicle_id,
                      vehicle_name: popupVehicle.vehicle_name,
                    })}
                    staffMutationBusy={swappingStaff}
                    deallocatingStaffId={deallocatingStaffId}
                    refreshWarning={vehicleDetailsRefreshWarning}
                    setupName={viewedSetup?.name}
                    readOnly={isLockedHistory}
                  />
                )}
                {/* Rendered after both detail modals so the picker stacks above either one. */}
                {swapSource && (selectedProject || popupVehicle) && !isLockedHistory && (
                  <StaffSwapModal key={`${selectedSetupId}-${swapSource.staff_id}-${swapSource.vehicle_id}`}
                    source={swapSource} loadData={loadSwapData} onSwap={swapStaff} onClose={closeSwap} />
                )}
                <Modal
                  title="Enter Password to Unlock"
                  open={passwordModalVisible}
                  onCancel={() => setPasswordModalVisible(false)}
                  onOk={async () => {
                    // Validate password
                    if (password) {  // Ensure a password is entered
                      await UnlockLoaderAllocation(unlockSiteId, password);  // Pass the password to the API call
                      setPasswordModalVisible(false); // Close the modal
                      setPassword(""); // Reset password input
                    } else {
                      message.error("Please enter a password");
                    }
                  }}
                >
                  <Input.Password
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </Modal>
                <Modal
                  title={(
                    <div className={styles.siteModalHeader}>
                      <span className={styles.vehicleModalEyebrow}>Replace Vehicle</span>
                      <div className={styles.siteModalTitleRow}>
                        <div className={styles.siteModalTitleContent}>
                          <h2 className={styles.vehicleModalTitle} title={vehicleToReplace?.vehicle_name || "Unnamed loader"}>
                            {vehicleToReplace?.vehicle_name || "Unnamed loader"}
                          </h2>
                          {vehicleToReplace && (
                            <p className={styles.vehicleModalSubtitle}>
                              {[vehicleToReplace.vehicle_type, vehicleToReplace.vehicle_subtype, vehicleToReplace.vehicle_model].filter(Boolean).join(" · ")}
                            </p>
                          )}
                        </div>
                        <div className={styles.vehicleModalTags} aria-label="Vehicle being replaced">
                          <Tag>Capacity: {formatArea(vehicleToReplace?.vehicle_capacity)}</Tag>
                          <Tag color={assignedReplacementCapacity > 0 ? "green" : "default"}>
                            Assigned: {formatArea(assignedReplacementCapacity)}
                          </Tag>
                        </div>
                      </div>
                    </div>
                  )}
                  open={isReplaceModalVisible}
                  width={960}
                  centered
                  className={styles.siteDetailsModal}
                  okText="Confirm replacement"
                  cancelText="Cancel"
                  confirmLoading={replacementBusy}
                  okButtonProps={{ disabled: !replacementPreview || replacementBusy || replacementUncertain || !availableReplacementVehicles.some(v => v.vehicle_id === selectedReplacementVehicle) }}
                  onCancel={() => { if (!replacementSubmitting.current) closeReplacement(); }}
                  onOk={() => replaceVehicle(vehicleToReplace.vehicle_id, selectedReplacementVehicle)}
                >
                  <div className={styles.siteModalContent}>
                    <section className={styles.vehicleModalSection} aria-labelledby="replace-current-heading">
                      <div className={styles.vehicleModalSectionHeading}>
                        <h3 id="replace-current-heading" className={styles.vehicleModalSectionTitle}>Current Site Allocations</h3>
                        <span>
                          {(vehicleToReplace?.allocated_sites || []).length} {(vehicleToReplace?.allocated_sites || []).length === 1 ? "site" : "sites"} · everything below moves to the replacement
                        </span>
                      </div>
                      {vehicleToReplace?.allocated_sites?.length > 0 ? (
                        <div className={styles.siteModalTableWrapper}>
                          <table className={`${styles.siteModalTable} ${styles.replaceModalTable}`}>
                            <thead>
                              <tr>
                                <th scope="col">Site</th>
                                <th scope="col">Loader capacity used</th>
                                <th scope="col">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {vehicleToReplace.allocated_sites.map((siteAllocation) => {
                                const site = sites.find(s => s.site_id === siteAllocation.site_id);
                                const siteName = siteAllocation.site_name || site?.site_name || `Site ID: ${siteAllocation.site_id}`;
                                const clientName = siteAllocation.client_name || site?.client_name;
                                return (
                                  <tr key={siteAllocation.site_id}>
                                    <td>
                                      <div className={styles.siteModalPrimaryCell}>
                                        <strong title={siteName}>{siteName}</strong>
                                        {clientName && <span className={styles.siteModalSecondary} title={clientName}>{clientName}</span>}
                                      </div>
                                    </td>
                                    <td>{formatArea(siteAllocation.allocated_capacity)}</td>
                                    <td>
                                      {Number(siteAllocation.allocated_capacity) === 0
                                        ? <Tag color="orange">Extra loader · 0 sq. ft</Tag>
                                        : <span className={styles.siteModalMuted}>Assigned</span>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className={styles.vehicleModalEmptyState}>This loader has no site allocations. Only its crew and attachments will move.</p>
                      )}
                    </section>

                    <Divider />

                    <section className={styles.vehicleModalSection} aria-labelledby="replace-crew-heading">
                      <div className={styles.vehicleModalSectionHeading}>
                        <h3 id="replace-crew-heading" className={styles.vehicleModalSectionTitle}>Crew and Attachments</h3>
                        <span>Exact units move with the loader</span>
                      </div>
                      <dl className={styles.siteOverviewGrid}>
                        <div className={styles.siteOverviewItem}>
                          <dt>Operator</dt>
                          {vehicleToReplace?.assigned_staff?.length > 0 ? (
                            vehicleToReplace.assigned_staff.map((staff) => (
                              <dd key={staff.staff_id} title={`${staff.name} (${staff.position})`}>{staff.name} ({staff.position})</dd>
                            ))
                          ) : (
                            <dd className={styles.siteModalMuted}>Not assigned</dd>
                          )}
                        </div>
                        <div className={styles.siteOverviewItem}>
                          <dt>Plow</dt>
                          {vehicleToReplace?.plow_size && vehicleToReplace.plow_size !== "n/a" ? (
                            <dd title={formatVehiclePlowLabel(vehicleToReplace)}>{formatVehiclePlowLabel(vehicleToReplace)}</dd>
                          ) : (
                            <dd className={styles.siteModalMuted}>Not assigned</dd>
                          )}
                        </div>
                        <div className={styles.siteOverviewItem}>
                          <dt>Double Down</dt>
                          {formatVehicleDoubleDownLabel(vehicleToReplace) ? (
                            <dd title={formatVehicleDoubleDownLabel(vehicleToReplace)}>{formatVehicleDoubleDownLabel(vehicleToReplace)}</dd>
                          ) : (
                            <dd className={styles.siteModalMuted}>Not assigned</dd>
                          )}
                        </div>
                      </dl>
                    </section>

                    <Divider />

                    <section className={styles.vehicleModalSection} aria-labelledby="replace-target-heading">
                      <div className={styles.vehicleModalSectionHeading}>
                        <h3 id="replace-target-heading" className={styles.vehicleModalSectionTitle}>Replacement Vehicle</h3>
                        <span>Compared with {formatArea(assignedReplacementCapacity)} currently assigned</span>
                      </div>
                      {replacementUncertain && (
                        <p className={styles.vehicleModalWarning} role="alert">
                          The last replacement request did not return a result. Refresh allocations before trying again.{" "}
                          <Button size="small" onClick={refreshReplacement}>Refresh allocations</Button>
                        </p>
                      )}
                      <Select
                        placeholder="Select a replacement vehicle"
                        value={selectedReplacementVehicle}
                        onChange={previewReplacement}
                        disabled={replacementSubmitting.current || replacementUncertain}
                        style={{ width: "100%" }}
                        popupMatchSelectWidth
                        listHeight={320}
                        aria-label="Select a replacement vehicle"
                      >
                        {availableReplacementVehicles.map((vehicle) => (
                          <Option key={vehicle.vehicle_id} value={vehicle.vehicle_id} title={replacementLabel(vehicle)}>
                            <span style={{ whiteSpace: "normal" }}>{replacementLabel(vehicle)}</span>
                          </Option>
                        ))}
                      </Select>
                      <p className={styles.replaceModalHelp}>
                        Only unassigned loaders are listed. Each option compares its total capacity with the work currently assigned; reservations are shown in the preview. Confirmation always uses the assignments as they are at that moment.
                      </p>
                    </section>

                    {replacementPreview && (
                      <>
                        <Divider />
                        <section className={styles.vehicleModalSection} aria-labelledby="replace-preview-heading" aria-label="Replacement preview">
                          <div className={styles.vehicleModalSectionHeading}>
                            <h3 id="replace-preview-heading" className={styles.vehicleModalSectionTitle}>Proposed Result</h3>
                            <span>
                              {availableReplacementVehicles.find(v => v.vehicle_id === selectedReplacementVehicle)?.vehicle_name || "Selected vehicle"}
                            </span>
                          </div>
                          {replacementPreview.warnings?.map((warning, index) => (
                            <p className={styles.vehicleModalWarning} role="alert" key={index}>{warning}</p>
                          ))}
                          {replacementPreview.committed_reduction > 0 && (
                            <p className={styles.siteModalDanger}>
                              The replacement holds {formatArea(replacementPreview.committed_reduction)} less than is currently assigned. Every site stays assigned, but that work will need to be covered elsewhere.
                            </p>
                          )}
                          {replacementPreview.committed_reduction === 0 && replacementPreview.site_credit_reduction > 0 && (
                            <p className={styles.siteModalNotice}>
                              Assigned work is unchanged, but {formatArea(replacementPreview.site_credit_reduction)} of reserved site coverage cannot be carried by the replacement.
                            </p>
                          )}
                          <div className={styles.vehicleCapacityStats}>
                            <div className={styles.vehicleCapacityStat}>
                              <span>Assigned after replacement</span>
                              <strong>{renderAreaChange(replacementPreview.assigned_capacity_before, replacementPreview.assigned_capacity_after)}</strong>
                            </div>
                            <div className={styles.vehicleCapacityStat}>
                              <span>Remaining capacity</span>
                              <strong>{formatArea(replacementPreview.remaining_capacity)}</strong>
                            </div>
                            <div className={styles.vehicleCapacityStat}>
                              <span>Committed capacity reduction</span>
                              <strong className={replacementPreview.committed_reduction > 0 ? styles.replaceModalLoss : undefined}>
                                {formatArea(replacementPreview.committed_reduction)}
                              </strong>
                            </div>
                          </div>
                          {replacementPreview.sites.length > 0 ? (
                            <div className={`${styles.siteModalTableWrapper} ${styles.replaceModalPreviewTable}`}>
                              <table className={`${styles.siteModalTable} ${styles.replaceModalTable}`}>
                                <thead>
                                  <tr>
                                    <th scope="col">Site</th>
                                    <th scope="col">Loader capacity used</th>
                                    <th scope="col">Site coverage</th>
                                    <th scope="col">Uncovered after replacement</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {replacementPreview.sites.map(site => (
                                    <tr key={site.site_id}>
                                      <td>
                                        <div className={styles.siteModalPrimaryCell}>
                                          <strong title={site.site_name}>{site.site_name}</strong>
                                          {site.overallocated_to_site && <Tag color="orange">Overallocated</Tag>}
                                        </div>
                                      </td>
                                      <td>{renderAreaChange(site.allocated_capacity, site.new_allocated_capacity)}</td>
                                      <td>{renderAreaChange(site.site_allocated_capacity, site.new_site_allocated_capacity)}</td>
                                      <td className={site.shortfall_after > (site.shortfall_before ?? 0) ? styles.replaceModalLoss : undefined}>
                                        {formatArea(site.shortfall_after)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p className={styles.vehicleModalEmptyState}>No site rows are affected.</p>
                          )}
                        </section>
                      </>
                    )}
                  </div>
                </Modal>
              </div>
            </div>
          </DndProvider>
        </Theme>
      </ConfigProvider>
    );
  };
  export default EquipmentAllocationTable;
